import { communityApi } from 'utils/api';

/** 서버 응답 → 프론트 필드 매핑 */
const normalizePost = (post) => {
  let mediaUrls = post.media_urls;
  if (typeof mediaUrls === 'string') {
    try { mediaUrls = JSON.parse(mediaUrls); } catch { mediaUrls = []; }
  }
  return {
    ...post,
    media_urls: mediaUrls || [],
    author_nickname: post.author_nickname || post.user?.nickname || '알 수 없음',
    author_name: post.author_name || post.user?.username || '',
    author_profile_image: post.author_profile_image || post.user?.profile_image || '',
    is_liked: !!post.is_liked,
    is_bookmarked: !!post.is_bookmarked,
  };
};

/** 서버 응답 → 프론트 댓글 필드 매핑 */
const normalizeComment = (comment) => ({
  ...comment,
  author_nickname: comment.author_nickname || comment.user?.nickname || '알 수 없음',
  author_profile_image: comment.author_profile_image || comment.user?.profile_image || '',
  is_liked: !!comment.is_liked,
  replies: (comment.replies || []).map(normalizeComment),
});

/** 게시글 목록 조회
 *  @param {object}  opts
 *  @param {string} [opts.postCategory] 카테고리 코드 (자유/질문/꿀팁/후기 …)
 *  @param {('foryou'|'latest')} [opts.sort] 정렬 — 추천(foryou) | 최신(latest).
 *    미지정 시 서버 기본값 foryou(인기 4 + 최신 6 인터리빙, 1페이지 한정 / 2페이지부터 최신 fallback).
 *    ADR-0004 (Community PR #28). 응답 meta.sort 로 적용값 echo.
 */
export const getFeed = async (page = 1, limit = 20, { postCategory, sort } = {}) => {
  let url = `/community/api/v1/feed?page=${page}&limit=${limit}`;
  if (postCategory) url += `&post_category=${encodeURIComponent(postCategory)}`;
  if (sort) url += `&sort=${encodeURIComponent(sort)}`;
  const res = await communityApi.get(url);
  const { meta, data, ...rest } = res.data;
  return {
    ...rest,
    data: (data || []).map(normalizePost),
    meta: {
      page: meta?.page ?? page,
      limit: meta?.limit ?? limit,
      total: meta?.count ?? 0,
      totalPages: meta?.hasMore ? page + 1 : page,
      sort: meta?.sort,
    },
  };
};

/** 활동유형별 카테고리 목록 조회 */
export const getCategories = async (activityTypeCode) => {
  const res = await communityApi.get(
    `/community/api/v1/activity-types/${activityTypeCode}/categories`
  );
  return res.data;
};

/** 유저 게시글 목록 조회 */
export const getUserPosts = async (userId, page = 1, limit = 20) => {
  const res = await communityApi.get(
    `/community/api/v1/users/${encodeURIComponent(userId)}/posts?page=${page}&limit=${limit}`
  );
  const { meta, data, ...rest } = res.data;
  return {
    ...rest,
    data: (data || []).map(normalizePost),
    meta: {
      page: meta?.page ?? page,
      limit: meta?.limit ?? limit,
      total: meta?.count ?? 0,
      totalPages: meta?.hasMore ? page + 1 : page,
    },
  };
};

/** 게시글 검색 */
export const searchPosts = async (keyword, page = 1, limit = 20) => {
  const res = await communityApi.get(
    `/community/api/v1/posts/search?q=${encodeURIComponent(keyword)}&page=${page}&limit=${limit}`
  );
  const { meta, data, ...rest } = res.data;
  return {
    ...rest,
    data: (data || []).map(normalizePost),
    meta: {
      page: meta?.page ?? page,
      limit: meta?.limit ?? limit,
      total: meta?.count ?? 0,
      totalPages: meta?.hasMore ? page + 1 : page,
    },
  };
};

/** 유저가 좋아요한 게시글 목록 조회 */
export const getUserLikedPosts = async (userId, page = 1, limit = 20) => {
  const res = await communityApi.get(
    `/community/api/v1/users/${encodeURIComponent(userId)}/liked-posts?page=${page}&limit=${limit}`
  );
  const { meta, data, ...rest } = res.data;
  return {
    ...rest,
    data: (data || []).map(normalizePost),
    meta: {
      page: meta?.page ?? page,
      limit: meta?.limit ?? limit,
      total: meta?.count ?? 0,
      totalPages: meta?.hasMore ? page + 1 : page,
    },
  };
};

/** 게시글 상세 조회 */
export const getPostDetail = async (postId) => {
  const res = await communityApi.get(`/community/api/v1/posts/${postId}`);
  const result = res.data;
  if (result.data) result.data = normalizePost(result.data);
  return result;
};

/** 게시글 좋아요 토글 */
export const togglePostLike = async (postId) => {
  const res = await communityApi.post(`/community/api/v1/posts/${postId}/like`);
  return res.data;
};

/** 댓글 좋아요 토글 */
export const toggleCommentLike = async (commentId) => {
  const res = await communityApi.post(`/community/api/v1/comments/${commentId}/like`);
  return res.data;
};

/** 이미지 업로드 */
export const uploadImages = async (files, { onUploadProgress } = {}) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const res = await communityApi.post('/community/api/v1/upload/images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
  return res.data;
};

/** 영상 업로드 */
export const uploadVideos = async (files, { onUploadProgress } = {}) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const res = await communityApi.post('/community/api/v1/upload/videos', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
  return res.data;
};

/** 게시글 작성 */
export const createPost = async (content, { mediaUrls = [], mediaType, activityType, postCategory } = {}) => {
  const body = { content };
  if (mediaUrls.length > 0) body.media_urls = mediaUrls;
  if (mediaType) body.media_type = mediaType;
  if (activityType) body.activity_type = activityType;
  if (postCategory) body.post_category = postCategory;
  const res = await communityApi.post('/community/api/v1/posts', body);
  return res.data;
};

/** 댓글 목록 조회 */
export const getComments = async (postId, page = 1, limit = 20) => {
  const res = await communityApi.get(
    `/community/api/v1/posts/${postId}/comments?page=${page}&limit=${limit}`
  );
  const { meta, data, ...rest } = res.data;
  const items = (data || []).map(normalizeComment);
  const hasMore = meta?.hasMore ?? items.length >= limit;
  return {
    ...rest,
    data: items,
    meta: {
      page: meta?.page ?? page,
      limit: meta?.limit ?? limit,
      total: meta?.total ?? 0,
      totalPages: hasMore ? page + 1 : page,
    },
  };
};

/** 게시글 수정 — PATCH /posts/:id */
export const updatePost = async (postId, { content, mediaUrls, mediaType, activityType, postCategory } = {}) => {
  const body = {};
  if (content !== undefined) body.content = content;
  if (mediaUrls !== undefined) body.media_urls = mediaUrls;
  if (mediaType !== undefined) body.media_type = mediaType;
  if (activityType !== undefined) body.activity_type = activityType;
  if (postCategory !== undefined) body.post_category = postCategory;
  const res = await communityApi.patch(`/community/api/v1/posts/${postId}`, body);
  const result = res.data;
  if (result.data) result.data = normalizePost(result.data);
  return result;
};

/** 게시글 삭제 — DELETE /posts/:id */
export const deletePost = async (postId) => {
  const res = await communityApi.delete(`/community/api/v1/posts/${postId}`);
  return res.data;
};

/** 댓글 수정 — PATCH /comments/:id */
export const updateComment = async (commentId, content) => {
  const res = await communityApi.patch(`/community/api/v1/comments/${commentId}`, { content });
  const result = res.data;
  if (result.data) result.data = normalizeComment(result.data);
  return result;
};

/** 댓글 삭제 — DELETE /comments/:id */
export const deleteComment = async (commentId) => {
  const res = await communityApi.delete(`/community/api/v1/comments/${commentId}`);
  return res.data;
};

// === 차단 ===

/** 유저 차단 */
export const blockUser = async (userId) => {
  const res = await communityApi.post(`/community/api/v1/users/${encodeURIComponent(userId)}/block`);
  return res.data;
};

/** 유저 차단 해제 */
export const unblockUser = async (userId) => {
  const res = await communityApi.delete(`/community/api/v1/users/${encodeURIComponent(userId)}/block`);
  return res.data;
};

/** 차단 목록 조회 */
export const getBlockedUsers = async (page = 1, limit = 20) => {
  const res = await communityApi.get(`/community/api/v1/users/blocked?page=${page}&limit=${limit}`);
  return res.data;
};

/** 차단 상태 확인 */
export const getBlockStatus = async (userId) => {
  const res = await communityApi.get(`/community/api/v1/users/${encodeURIComponent(userId)}/block/status`);
  return res.data;
};

// === 신고 ===

/** 신고하기 */
export const createReport = async ({ target_type, target_id, reason, description }) => {
  const body = { target_type, target_id, reason };
  if (description) body.description = description;
  const res = await communityApi.post('/community/api/v1/reports', body);
  return res.data;
};

/** 내 신고 목록 조회 */
export const getMyReports = async (page = 1, limit = 20) => {
  const res = await communityApi.get(
    `/community/api/v1/reports/me?page=${page}&limit=${limit}`
  );
  return res.data;
};

/** [관리자] 전체 신고 목록 조회 */
export const getAdminReports = async ({ page = 1, limit = 20, status, target_type } = {}) => {
  let url = `/community/api/v1/admin/reports?page=${page}&limit=${limit}`;
  if (status) url += `&status=${encodeURIComponent(status)}`;
  if (target_type) url += `&target_type=${encodeURIComponent(target_type)}`;
  const res = await communityApi.get(url);
  return res.data;
};

/** [관리자] 신고 상세 조회 */
export const getAdminReportDetail = async (reportId) => {
  const res = await communityApi.get(`/community/api/v1/admin/reports/${reportId}`);
  return res.data;
};

/** [관리자] 신고 상태 변경 */
export const updateReportStatus = async (reportId, status) => {
  const res = await communityApi.patch(
    `/community/api/v1/admin/reports/${reportId}/status`,
    { status }
  );
  return res.data;
};

/** [관리자] 대상의 신고 일괄 처리 */
export const resolveReportsByTarget = async ({ target_type, target_id, status }) => {
  const res = await communityApi.post('/community/api/v1/admin/reports/resolve-target', {
    target_type,
    target_id,
    status,
  });
  return res.data;
};

/** 댓글 작성 */
export const createComment = async (postId, content, parentId = null) => {
  const body = { content };
  if (parentId) body.parent_id = parentId;
  const res = await communityApi.post(`/community/api/v1/posts/${postId}/comments`, body);
  const result = res.data;
  if (result.data) result.data = normalizeComment(result.data);
  return result;
};

// === 알림 ===

/** 커뮤니티 알림 목록 조회 */
export const getCommunityNotifications = async (page = 1, limit = 20) => {
  const res = await communityApi.get(`/community/api/v1/notifications?page=${page}&limit=${limit}`);
  return res.data;
};

/** 커뮤니티 안읽은 알림 수 */
export const getCommunityUnreadCount = async () => {
  const res = await communityApi.get(`/community/api/v1/notifications/unread-count`);
  return res.data;
};

/** 커뮤니티 알림 읽음 처리 */
export const markCommunityNotificationAsRead = async (notificationId) => {
  const res = await communityApi.post(`/community/api/v1/notifications/${notificationId}/read`);
  return res.data;
};

/** 커뮤니티 알림 전체 읽음 */
export const markAllCommunityNotificationsAsRead = async () => {
  const res = await communityApi.post(`/community/api/v1/notifications/read-all`);
  return res.data;
};

// === 멘션 자동완성 ===

/** 멘션 자동완성용 사용자 검색 (ADR-0001, GET /community/api/v1/users/search).
 *  @param {string} query 닉네임/이름 prefix
 *  @returns {Promise<{ users: Array, rateLimited?: boolean }>}
 */
export const searchMentionUsers = async (query) => {
  try {
    const res = await communityApi.get(`/community/api/v1/users/search`, {
      params: { q: query },
    });
    return { users: res.data?.data || [] };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 419 || status === 429) return { users: [], rateLimited: true };
    return { users: [] };
  }
};
